// Wie man auf diesem Gerät diktiert – ein kurzer Hinweis für die Textfelder
// der Fragen (Gernot beim Augentest E3, 21.10.2026: „ein kleiner Hinweis mit
// Windows-Taste H oder beim MacBook …, am iPhone das Mikrofon der Tastatur“).
// Nur ein Hinweis: Das Diktat macht das Gerät selbst, die App nimmt nichts
// auf und schickt nichts weg.

/** Woran das Gerät zu erkennen ist – der User-Agent und ob es einen Touchscreen hat. */
export interface GeraetMerkmale {
  ua: string;
  /** navigator.maxTouchPoints – ein iPad meldet sich als „Macintosh“, hat aber Berührung. */
  beruehrpunkte: number;
}

function merkmaleHier(): GeraetMerkmale {
  if (typeof navigator === "undefined") return { ua: "", beruehrpunkte: 0 };
  return { ua: navigator.userAgent, beruehrpunkte: navigator.maxTouchPoints };
}

/**
 * Der Hinweis in wenigen Worten, etwa „Windows-Taste + H“ – oder leer, wenn
 * das Gerät unbekannt ist (dann lieber nichts als etwas Falsches).
 */
export function diktatHinweis(m: GeraetMerkmale = merkmaleHier()): string {
  if (/iPhone|iPad|iPod|Android/i.test(m.ua)) return "Mikrofon der Tastatur";
  if (/Macintosh/i.test(m.ua)) return m.beruehrpunkte > 1 ? "Mikrofon der Tastatur" : "zweimal die Fn-Taste";
  if (/Windows/i.test(m.ua)) return "Windows-Taste + H";
  return "";
}

/** Der Platzhalter für ein Textfeld: „Antworten – tippen oder diktieren (Windows-Taste + H)“. */
export function mitDiktat(anfang: string, m: GeraetMerkmale = merkmaleHier()): string {
  const hinweis = diktatHinweis(m);
  return hinweis ? `${anfang} – tippen oder diktieren (${hinweis})` : `${anfang} – tippen`;
}

/**
 * Wie das Diktieren auf diesem Gerät geht – ein Satz zum Aufklappen unter
 * einem Textfeld (Gernot, 22.10.2026: „es steht nirgends, wie das
 * Diktieren geht“; Manuel hat am iPad „Einsprechen“ für Diktieren
 * gehalten). Je Gerät nur der eine passende Satz – Handy und Tablet in der
 * Doku-App, Windows und Mac am Laptop im CRM ergeben sich von selbst.
 */
export function diktatAnleitung(m: GeraetMerkmale = merkmaleHier()): string {
  if (/iPhone|iPad|iPod/i.test(m.ua) || (/Macintosh/i.test(m.ua) && m.beruehrpunkte > 1)) {
    return "Ins Feld tippen, dann auf das Mikrofon der Tastatur – sprechen, fertig. Kein Mikrofon da? Einstellungen → Allgemein → Tastatur → „Diktierfunktion“ einschalten.";
  }
  if (/Android/i.test(m.ua)) return "Ins Feld tippen, dann auf das Mikrofon der Tastatur – sprechen, fertig.";
  if (/Macintosh/i.test(m.ua)) return "Ins Feld klicken, dann zweimal die Fn-Taste drücken – sprechen, fertig.";
  if (/Windows/i.test(m.ua)) {
    // Seit E3b (O81 b, Gernot am 22.10.2026): Windows diktiert nicht auf Deutsch (Österreich) – das trifft in Österreich fast jeden Laptop.
    const oesterreich =
      " Sagt Windows „in der aktuellen Sprache nicht verfügbar“: unter Einstellungen → Zeit und Sprache → Sprache „Deutsch (Deutschland)“ hinzufügen und vor dem Diktieren mit Windows-Taste + Leertaste dorthin wechseln.";
    return (
      (m.beruehrpunkte > 1
        ? "Ins Feld tippen, dann Windows-Taste + H – oder das Mikrofon der Bildschirmtastatur. Sprechen, fertig."
        : "Ins Feld klicken, dann Windows-Taste + H – sprechen, fertig.") + oesterreich
    );
  }
  return "";
}
